import type { PayrollPeriod } from "../../services/financialService";
import { StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/** Where a pay run is: its approval step until approved, then how much of it staff have been paid. */
export function payrollStage(period: PayrollPeriod): { key: string; label: string; dot: string } {
  const workflow = period.workflow_status ?? "";
  if (period.status === "cancelled" || workflow === "cancelled") return { key: "cancelled", label: "Cancelled", dot: "bg-gray-400" };
  if (workflow === "returned") return { key: "returned", label: "Returned for changes", dot: "bg-amber-500" };
  if (workflow === "submitted") return { key: "submitted", label: "Awaiting review", dot: "bg-sky-500" };
  if (workflow === "verified") return { key: "verified", label: "Awaiting approval", dot: "bg-indigo-500" };
  if (workflow === "rejected") return { key: "rejected", label: "Rejected", dot: "bg-red-500" };
  if (period.status !== "approved") return { key: "draft", label: "Draft", dot: "bg-gray-400" };
  if (period.payment_status === "paid") return { key: "paid", label: "Paid", dot: "bg-emerald-500" };
  if (period.payment_status === "part_paid") return { key: "part_paid", label: "Part paid", dot: "bg-amber-500" };
  return { key: "unpaid", label: "Approved, not yet paid", dot: "bg-amber-500" };
}

const gross = (period: PayrollPeriod) => period.payslips.reduce((sum, slip) => sum + Number(slip.gross_salary), 0);

export const payrollConfig: RegisterConfig<PayrollPeriod> = {
  search: (period) => [period.name, period.notes, period.prepared_by_name, ...period.payslips.map((slip) => slip.staff_name)].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "preparing", label: "Drafts & returned", match: (period) => ["draft", "returned"].includes(payrollStage(period).key) },
    { key: "review", label: "Awaiting review", match: (period) => period.workflow_status === "submitted" },
    { key: "approval", label: "Awaiting approval", match: (period) => period.workflow_status === "verified" },
    { key: "topay", label: "To pay", match: (period) => ["unpaid", "part_paid"].includes(payrollStage(period).key) },
    { key: "paid", label: "Paid", match: (period) => payrollStage(period).key === "paid" },
  ],
  date: { label: "Month", value: (period) => period.period_start },
  sorts: [
    { key: "-month", label: "Newest month first", compare: reversed(byText<PayrollPeriod>((period) => period.period_start)) },
    { key: "month", label: "Oldest month first", compare: byText<PayrollPeriod>((period) => period.period_start) },
    { key: "-net", label: "Net pay (highest first)", compare: reversed(byNumber<PayrollPeriod>((period) => period.total_net_pay)) },
  ],
};

const money = (value: string | number) => Number(value || 0).toLocaleString();

export const payrollColumns: RegisterColumn<PayrollPeriod>[] = [
  { header: "Run", cell: (period) => <span className="font-medium text-gray-900">{period.name}</span>, exportValue: (period) => period.name },
  { header: "Month", cell: (period) => `${period.period_start} → ${period.period_end}`, exportValue: (period) => `${period.period_start} to ${period.period_end}`, className: "tabular-nums text-gray-600" },
  { header: "Staff", cell: (period) => period.staff_count, exportValue: (period) => period.staff_count, align: "right" },
  { header: "Gross (UGX)", cell: (period) => money(gross(period)), exportValue: gross, align: "right" },
  { header: "Net pay (UGX)", cell: (period) => <span className="font-semibold text-gray-900">{money(period.total_net_pay)}</span>, exportValue: (period) => Number(period.total_net_pay), align: "right" },
  {
    header: "Still owed (UGX)",
    cell: (period) => (period.status === "approved" && Number(period.balance_due) > 0 ? <span className="font-medium text-amber-700">{money(period.balance_due)}</span> : <span className="text-gray-300">—</span>),
    exportValue: (period) => (period.status === "approved" ? Number(period.balance_due) : 0),
    align: "right",
  },
  { header: "Stage", cell: (period) => <StatusDot {...payrollStage(period)} />, exportValue: (period) => payrollStage(period).label },
];
